import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/contexts/RoleContext";
import { MainLayout } from "@/layouts/MainLayout";
import { DataTable, FilterPanel, CallAgentButton } from "@/components/common";
import type { Column, FilterConfig } from "@/components/common";
import { ingressSourceLabel } from "@/utils/ingressSource";
import { productLeadsApi, utmOptionsApi, bureauApi, getCachedBureauReport } from "@/api";
import { productsApi } from "@/api/products.api";
import { CreateUtmLinkModal } from "@/components/CreateUtmLinkModal";
import { CreditDetailsModal } from "@/components/CreditDetailsModal";
import { LinkIcon, PlusIcon, ExcelIcon } from "@/components/icons";
import type { ProductLead } from "@/types/productLeads.types";
import type { BureauReportData } from "@/types/bureau.types";
import type { UtmOptions } from "@/types/utmOptions.types";
import { exportToExcel, type ExportColumn } from "@/utils/exportToExcel";
import { fetchAllForExport, EXPORT_PAGE_SIZE } from "@/utils/fetchAllForExport";
import styles from "./Leads.module.css";

const LEAD_STATUS_OPTIONS = [
  { label: "Initiated", value: "INITIATED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Expired", value: "EXPIRED" },
];

const SEARCH_TYPE_OPTIONS = [
  { label: "Mobile Number", value: "number" },
  { label: "Name", value: "name" },
  { label: "User ID", value: "id" },
  { label: "Email", value: "email" },
  { label: "Agent Name", value: "agent_name" },
  { label: "Tracking ID", value: "tracking_id" },
  { label: "Token", value: "token" },
  { label: "Sub", value: "sub" },
];

type LeadsSearchType =
  | "number"
  | "name"
  | "id"
  | "email"
  | "agent_name"
  | "tracking_id"
  | "token"
  | "sub";

// Build a display name from flattened extra_fields keys.
const buildName = (row: ProductLead): string => {
  // Prefer the backend-resolved customer_name (from users_), else the form
  // fields flattened out of extra_fields (portal leads before users_ resolve).
  const resolved = (row.customer_name ?? "") as string;
  if (resolved.trim()) return resolved.trim();
  const first = (row.firstName ?? row.fname ?? "") as string;
  const last = (row.lastName ?? row.lname ?? "") as string;
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || "-";
};

const STATIC_COLUMNS: Column<ProductLead>[] = [
  {
    key: "name",
    label: "Name",
    sortable: false,
    defaultVisible: true,
    render: (_, row) => buildName(row),
  },
  {
    key: "mobile",
    label: "Mobile",
    sortable: false,
    defaultVisible: true,
    // backend resolves customer_mob_num (users_ or click); fall back to the
    // form value flattened from extra_fields.
    render: (_, row) =>
      String(row.customer_mob_num || row.mobile || row.mob_num || "-"),
  },
  {
    key: "email",
    label: "Email",
    sortable: false,
    defaultVisible: true,
    render: (_, row) => String(row.customer_email || row.email || "-"),
  },
  {
    key: "product_id",
    label: "Product Id",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "tracking_id",
    label: "Tracking Id",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "mis_status",
    label: "MIS Status",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      const v = String(value || "Pending");
      return (
        <span
          style={{
            color: v === "Pending" ? "#9ca3af" : "#111827",
            fontWeight: 500,
          }}
        >
          {v}
        </span>
      );
    },
  },
  {
    key: "agent_name",
    label: "Sub Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.agent_mob_num}
      />
    ),
  },
  {
    key: "parent_agent_name",
    label: "Parent Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.parent_agent_mob_num}
      />
    ),
  },
  {
    key: "created_at",
    label: "Created At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "updated_at",
    label: "Updated At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "user_id",
    label: "User ID",
    sortable: true,
    defaultVisible: false,
    render: (_, row) => String(row.user_id ?? "-"),
  },
  {
    key: "source",
    label: "Source",
    sortable: true,
    defaultVisible: false,
    render: (value) => ingressSourceLabel(value),
  },
];

// Frontend-only column removals (API still returns these keys). Kept out of
// both static columns and the dynamic-column picker.
const HIDDEN_KEYS = new Set<string>([
  // common removals (clicks + leads)
  "id",
  "is_active",
  "evaluation_id",
  "token",
  "vendor_status",
  "description",
  "agent_mob_num",
  "agent_email",
  "parent_agent_mob_num",
  "source_product_id",
  // leads-only removals
  "lead_id",
  "status",
  "redirect_link",
  "product_code",
]);

const FILTER_CONFIGS_BASE: FilterConfig[] = [
  {
    key: "search_type",
    label: "Search Type",
    type: "select",
    options: SEARCH_TYPE_OPTIONS,
  },
  {
    key: "search_text",
    label: "Search",
    type: "text",
    placeholder: "Enter search text...",
  },
  {
    key: "status",
    label: "Lead Status",
    type: "multiSelect",
    options: LEAD_STATUS_OPTIONS,
  },
  {
    key: "date_range",
    label: "Date Range",
    type: "dateRangeField",
    dateRangeFields: [
      { label: "Created At", value: "created_at" },
      { label: "Updated At", value: "updated_at" },
    ],
  },
  {
    key: "product_id",
    label: "Product",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search products...",
  },
  {
    key: "campaign_id",
    label: "Campaign",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search campaign...",
  },
  {
    key: "affiliate_code",
    label: "Affiliate Code",
    type: "text",
    placeholder: "Partial match...",
  },
];

export const ProductLeads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { selectedRole } = useRole();
  const [leads, setLeads] = useState<ProductLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;
  const agentMobNum = searchParams.get("agentMobNum") || null;
  const agentEmail = searchParams.get("agentEmail") || null;

  // Campaign filter is admin-only (selfOnboard).
  const isSelfOnboard =
    (localStorage.getItem("agent_id") || localStorage.getItem("agentId")) ===
    "selfOnboard";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState<boolean | undefined>(undefined);

  const [exportLimit, setExportLimit] = useState<number>(10);
  const [exporting, setExporting] = useState(false);
  const [showUtmModal, setShowUtmModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Credit details modal state (bureau report per lead's user_id)
  const [creditModal, setCreditModal] = useState<{
    open: boolean;
    loading: boolean;
    error: string;
    data: BureauReportData | null;
    userId: string | null;
  }>({ open: false, loading: false, error: "", data: null, userId: null });

  const handleCreditDetails = useCallback(async (row: ProductLead) => {
    const userId = row.user_id;
    if (!userId) return;

    // Cached in sessionStorage until the tab closes — avoids refetching.
    const cached = getCachedBureauReport(userId);
    if (cached) {
      setCreditModal({ open: true, loading: false, error: "", data: cached, userId });
      return;
    }

    setCreditModal({ open: true, loading: true, error: "", data: null, userId });
    try {
      const data = await bureauApi.generateBureauReport(userId);
      setCreditModal({ open: true, loading: false, error: "", data, userId });
    } catch (err) {
      setCreditModal({
        open: true,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to fetch credit details",
        data: null,
        userId,
      });
    }
  }, []);

  const handleCopyAffiliateLink = () => {
    const id =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId") ||
      "";
    navigator.clipboard.writeText(`https://login.clickpe.ai/?agentId=${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [filters, setFilters] = useState<Record<string, unknown>>(() => {
    const status = searchParams.getAll("status");
    const mis_status = searchParams.getAll("mis_status");
    const product_id = searchParams.getAll("product_id");
    const date_range = searchParams.getAll("date_range");
    return {
      status: status.length > 0 ? status : [],
      mis_status: mis_status.length > 0 ? mis_status : [],
      product_id: product_id.length > 0 ? product_id : [],
      date_range_field: searchParams.get("date_range_field") || null,
      date_range: date_range.length === 2 ? date_range : null,
      search_text: searchParams.get("search_text") || "",
      search_type: searchParams.get("search_type") || null,
      campaign_id: searchParams.getAll("campaign_id"),
      affiliate_code: searchParams.get("affiliate_code") || "",
    };
  });

  const [searchTextInput, setSearchTextInput] = useState(
    () => searchParams.get("search_text") || ""
  );

  const [productFilterOptions, setProductFilterOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const [utmOptions, setUtmOptions] = useState<UtmOptions>({
    utm_source: [],
    utm_medium: [],
    utm_term: [],
    utm_content: [],
    campaign_id: [],
    sub: [],
  });

  const FILTER_CONFIGS: FilterConfig[] = useMemo(
    () =>
      FILTER_CONFIGS_BASE.filter(
        (f) => f.key !== "campaign_id" || isSelfOnboard
      ).map((f) => {
        if (f.key === "product_id") {
          return { ...f, options: productFilterOptions };
        }
        if (f.key === "campaign_id") {
          const vals = utmOptions.campaign_id;
          return { ...f, options: vals.map((v) => ({ label: v, value: v })) };
        }
        return f;
      }),
    [productFilterOptions, utmOptions, isSelfOnboard],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchProductOptions = async () => {
      try {
        const result = await productsApi.getProducts({ channel: "AGENT" });
        if (result.status === "Success" && Array.isArray(result.response)) {
          setProductFilterOptions(
            result.response.map((p) => ({
              label: `${p.name} (${p.code || p.id})`,
              value: p.id,
            })),
          );
        }
      } catch {
        // silently keep empty options
      }
    };
    fetchProductOptions();
  }, []);

  useEffect(() => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");
    if (!agentId) return;
    utmOptionsApi
      .getUtmOptions({ agent_id: agentId, scope: "leads" })
      .then(setUtmOptions);
  }, [agentIdOverride]);

  const buildBaseRequest = useCallback(
    (agentId: string, currentPage: number, currentPageSize: number) => {
      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
              filters.date_range[0] as string | null,
              filters.date_range[1] as string | null,
            ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];

      const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
        ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
        : undefined;

      const commonFilters = {
        status:
          Array.isArray(filters.status) && filters.status.length > 0
            ? (filters.status as string[])
            : undefined,
        mis_status:
          Array.isArray(filters.mis_status) && filters.mis_status.length > 0
            ? (filters.mis_status as string[])
            : undefined,
        product_id:
          Array.isArray(filters.product_id) && filters.product_id.length > 0
            ? (filters.product_id as string[])
            : undefined,
        campaign_id:
          Array.isArray(filters.campaign_id) && filters.campaign_id.length > 0
            ? (filters.campaign_id as string[])
            : undefined,
      };

      const affiliateCode = String(filters.affiliate_code || "").trim();
      const likeFilters = affiliateCode
        ? { affiliate_code: affiliateCode }
        : undefined;

      return {
        agent_id: agentId,
        page: currentPage,
        page_size: currentPageSize,
        ranges: {
          created_at: createdAtRange,
          updated_at: updatedAtRange,
        },
        sort_by: sortByValue,
        filters: commonFilters,
        like_filters: likeFilters,
      };
    },
    [filters, sortBy, sortOrder]
  );

  const fetchLeads = useCallback(async () => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");

    if (!agentId) {
      setError("Agent ID not found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;

      const searchTypeValue: LeadsSearchType | undefined = searchType
        ? (searchType as LeadsSearchType)
        : undefined;

      const request = {
        ...buildBaseRequest(agentId, page, pageSize),
        search_text: searchText || undefined,
        search_type: searchTypeValue,
      };

      const response = await productLeadsApi.getProductLeads(request);

      if (response.response) {
        setLeads(response.response);
        setHasMore(response.pagination_metadata?.has_more);
      } else {
        setError(response.message || "Failed to fetch product leads");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch product leads"
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, buildBaseRequest, filters, agentIdOverride]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated, fetchLeads, agentIdOverride]);

  const handleSort = (key: string, order: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === "search_text") {
      const text = typeof value === "string" ? value : "";
      setSearchTextInput(text);
      if (!text) {
        setFilters((prev) => ({ ...prev, search_text: "" }));
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("search_text");
          return newParams;
        });
      }
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((v) => newParams.append(key, String(v)));
      } else if (value !== null && value !== undefined && value !== "") {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  const handleSearchSubmit = (_key: string, value: unknown) => {
    const text = typeof value === "string" ? value : "";
    setFilters((prev) => ({
      ...prev,
      search_text: text,
    }));
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (text) {
        newParams.set("search_text", text);
      } else {
        newParams.delete("search_text");
      }
      return newParams;
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchTextInput("");
    setFilters({
      status: [],
      mis_status: [],
      product_id: [],
      date_range_field: null,
      date_range: null,
      search_text: "",
      search_type: null,
      campaign_id: [],
      affiliate_code: "",
    });
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      [
        "status",
        "mis_status",
        "date_range_field",
        "date_range",
        "search_text",
        "search_type",
        "product_id",
        "campaign_id",
        "affiliate_code",
      ].forEach((k) => newParams.delete(k));
      return newParams;
    });
  };

  const handleExportToExcel = async () => {
    const tableId = "product_leads";
    const savedConfigStr = localStorage.getItem(`table_config_${tableId}`);
    let targetColumns = allColumns;

    if (savedConfigStr) {
      try {
        const config = JSON.parse(savedConfigStr);
        if (config.visibleColumns && Array.isArray(config.visibleColumns)) {
          const visibleSet = new Set(config.visibleColumns);
          targetColumns = allColumns.filter((col) => visibleSet.has(col.key));

          if (config.columnOrder && Array.isArray(config.columnOrder)) {
            targetColumns.sort((a, b) => {
              const indexA = config.columnOrder.indexOf(a.key);
              const indexB = config.columnOrder.indexOf(b.key);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          }
        }
      } catch (e) {
        console.error(
          "Error parsing saved config for export, using default.",
          e
        );
        targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
      }
    } else {
      targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
    }

    setExporting(true);
    setError("");

    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");

      if (!agentId) throw new Error("Agent ID not found");

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const searchTypeValue: LeadsSearchType | undefined = searchType
        ? (searchType as LeadsSearchType)
        : undefined;

      const dataToExport = await fetchAllForExport<ProductLead>(
        async (page, pageSize) => {
          const res = await productLeadsApi.getProductLeads({
            ...buildBaseRequest(agentId, page, pageSize),
            search_text: searchText || undefined,
            search_type: searchTypeValue,
          });
          if (!res.response) {
            throw new Error(
              res.message || "Failed to fetch product leads data for export"
            );
          }
          return {
            rows: res.response,
            hasMore: res.pagination_metadata?.has_more,
          };
        },
        exportLimit,
        EXPORT_PAGE_SIZE
      );

      if (dataToExport.length === 0) {
        alert("No data found to export.");
        return;
      }

      const exportColumns: ExportColumn<ProductLead>[] = targetColumns.map(
        (col) => {
          if (!col.render) return { key: col.key, label: col.label };
          return {
            key: col.key,
            label: col.label,
            render: (value: unknown, row: ProductLead) => {
              const rawValue = (row as unknown as Record<string, unknown>)[
                col.key
              ];
              if (col.key === "name") return buildName(row);
              if (col.key === "mobile")
                return String(row.mobile ?? row.mob_num ?? "-");
              if (col.key === "mis_status")
                return String(rawValue || "Pending");
              if (
                col.key.includes("_date") ||
                col.key.includes("created_at") ||
                col.key.includes("updated_at")
              ) {
                if (!rawValue) return "-";
                try {
                  return new Date(rawValue as string).toLocaleString();
                } catch {
                  return String(rawValue);
                }
              }
              if (rawValue === null || rawValue === undefined) return "-";
              return String(rawValue);
            },
          };
        }
      );

      exportToExcel(
        dataToExport as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        "product_leads"
      );
    } catch (err) {
      console.error(err);
      const errorMsg =
        err instanceof Error ? err.message : "Failed to export data";
      setError(errorMsg);
      alert(`Export Failed: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const dynamicColumns = useMemo(() => {
    if (leads.length === 0) return [];
    // Union of keys across the page so per-product extra_fields all surface.
    const allKeys = new Set<string>();
    leads.forEach((lead) => {
      Object.keys(lead || {}).forEach((k) => allKeys.add(k));
    });
    const definedKeys = new Set(STATIC_COLUMNS.map((col) => col.key));
    // These are consumed by static columns' render() and shouldn't double up.
    // `extra_fields` is the raw JSONB object (renders as [object Object]) — its
    // contents are already flattened into top-level keys, so skip it.
    ["firstName", "lastName", "fname", "lname", "mob_num", "extra_fields"].forEach((k) =>
      definedKeys.add(k)
    );
    const remainingKeys = Array.from(allKeys).filter(
      (key) => !definedKeys.has(key) && !HIDDEN_KEYS.has(key)
    );
    return remainingKeys.map((key) => {
      const label = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      let render:
        | ((value: unknown, row: ProductLead) => React.ReactNode)
        | undefined;
      if (
        key.includes("_amt") ||
        key.includes("amount") ||
        key.includes("income") ||
        key.includes("Amount") ||
        key.includes("Income")
      )
        render = (value) =>
          value ? `₹${Number(value).toLocaleString()}` : "-";
      else if (
        key.includes("_date") ||
        key.includes("created_at") ||
        key.includes("updated_at") ||
        key === "dob"
      )
        render = (value) => {
          try {
            return value ? new Date(value as string).toLocaleDateString() : "-";
          } catch {
            return String(value);
          }
        };
      else
        render = (value) => {
          if (value === null || value === undefined) return "-";
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        };
      return {
        key,
        label,
        sortable: false,
        defaultVisible: false,
        render,
      } as Column<ProductLead>;
    });
  }, [leads]);

  const allColumns = useMemo(
    () => [...STATIC_COLUMNS, ...dynamicColumns],
    [dynamicColumns]
  );

  if (!isAuthenticated) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Product Leads
              {agentName && (
                <span className={styles.agentContext}>({agentName})</span>
              )}
            </h1>
            {agentName && (
              <p className={styles.agentContextInfo}>
                Viewing product leads for: {agentName} (ID: {agentIdOverride})
              </p>
            )}
          </div>

          <div className={styles.headerControls}>
            <button
              className={`${styles.affiliateLinkButton} ${
                copied ? styles.copied : ""
              }`}
              onClick={handleCopyAffiliateLink}
              title="Copy your affiliate link to clipboard"
            >
              <LinkIcon size={18} />
              {copied ? "Copied!" : "Copy Affiliate Link"}
            </button>
            <button
              className={styles.primaryToolbarButton}
              onClick={() => setShowUtmModal(true)}
              title="Create a distributable UTM link"
            >
              <PlusIcon size={18} />
              Create UTM Link
            </button>
            <div className={styles.exportToolbar}>
              <select
                value={exportLimit}
                onChange={(e) => setExportLimit(Number(e.target.value))}
                className={styles.limitSelect}
                title="Select number of records to export"
              >
                <option value={10}>Export 10</option>
                <option value={50}>Export 50</option>
                <option value={100}>Export 100</option>
                <option value={500}>Export 500</option>
                <option value={1000}>Export 1000</option>
                <option value={5000}>Export 5000</option>
              </select>
              <button
                className={styles.exportButton}
                onClick={() => handleExportToExcel()}
                disabled={loading || exporting || leads.length === 0}
                title="Export to Excel"
              >
                {exporting ? (
                  "Exporting..."
                ) : (
                  <>
                    <ExcelIcon size={18} />
                    Export to Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <FilterPanel
          filters={FILTER_CONFIGS}
          values={{
            ...filters,
            search_text: searchTextInput,
          }}
          onChange={handleFilterChange}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleResetFilters}
        />
        <DataTable
          data={leads}
          columns={allColumns}
          loading={loading}
          tableId="product_leads"
          pagination={{
            page,
            pageSize,
            hasMore,
            onPageChange: setPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
          }}
          sorting={{ sortBy, sortOrder, onSort: handleSort }}
          onRowActions={[
            {
              label: "Resume Journey",
              variant: "primary",
              // ponytail: link lives in redirect_link, older rows use description
              onClick: (row) => {
                const link = (row.redirect_link || row.description) as
                  | string
                  | undefined;
                if (link) {
                  const w = window.open(link, "_blank");
                  if (w) w.opener = null;
                }
              },
            },
            // Credit Details is restricted to the document verifier role
            ...(selectedRole?.role_type === "DOCUMENT_VERIFIER"
              ? [
                  {
                    label: "Credit Details",
                    variant: "secondary" as const,
                    disabled: (row: ProductLead) => !row.user_id,
                    onClick: handleCreditDetails,
                  },
                ]
              : []),
          ]}
          emptyMessage="No product leads found"
        />
      </div>
      {showUtmModal && (
        <CreateUtmLinkModal
          agentId={
            agentIdOverride ||
            localStorage.getItem("agent_id") ||
            localStorage.getItem("agentId") ||
            ""
          }
          onClose={() => setShowUtmModal(false)}
        />
      )}
      {creditModal.open && (
        <CreditDetailsModal
          loading={creditModal.loading}
          error={creditModal.error}
          data={creditModal.data}
          onClose={() =>
            setCreditModal((prev) => ({ ...prev, open: false }))
          }
          onRetry={() => {
            const lead = leads.find((l) => l.user_id === creditModal.userId);
            if (lead) handleCreditDetails(lead);
          }}
        />
      )}
    </MainLayout>
  );
};
